function isProbablyLocalhostHost(host: string): boolean {
  return (
    host === 'localhost' ||
    host.startsWith('localhost:') ||
    host === '127.0.0.1' ||
    host.startsWith('127.0.0.1:') ||
    host === '::1' ||
    host.startsWith('::1:') ||
    host === '[::1]' ||
    host.startsWith('[::1]:')
  );
}

function isDevMode(): boolean {
  try {
    return !!import.meta.env.DEV;
  } catch {
    return false;
  }
}

function shouldAllowInsecureScheme(host: string): boolean {
  return isDevMode() || isProbablyLocalhostHost(host);
}

function normalizeOriginProtocol(origin: URL): URL {
  if (origin.protocol === 'https:' || shouldAllowInsecureScheme(origin.host)) {
    return origin;
  }

  if (origin.protocol === 'http:') {
    origin.protocol = 'https:';
  }
  return origin;
}

/**
 * Normalize an input that may be:
 * - full URL (http/https)
 * - host[:port] (no scheme)
 *
 * Returns a canonical base URL without trailing slash.
 */
export function normalizeServerUrl(input?: string): string | undefined {
  const raw = (input || '').trim();
  if (!raw) return undefined;

  const noTrailingSlash = raw.replace(/\/+$/, '');

  if (noTrailingSlash.startsWith('http://') || noTrailingSlash.startsWith('https://')) {
    try {
      // Ensure it parses and drop any path/query/hash by keeping origin.
      const u = new URL(noTrailingSlash);
      return normalizeOriginProtocol(u).origin;
    } catch {
      if (noTrailingSlash.startsWith('http://') && !shouldAllowInsecureScheme(noTrailingSlash.slice('http://'.length))) {
        return `https://${noTrailingSlash.slice('http://'.length)}`;
      }
      return noTrailingSlash;
    }
  }

  const scheme = shouldAllowInsecureScheme(noTrailingSlash) ? 'http' : 'https';
  try {
    const u = new URL(`${scheme}://${noTrailingSlash}`);
    return u.origin;
  } catch {
    return `${scheme}://${noTrailingSlash}`.replace(/\/+$/, '');
  }
}

/**
 * Server key used everywhere in the client: strictly the URL origin.
 */
export function serverKeyFromServerUrl(input?: string): string | undefined {
  return normalizeServerUrl(input);
}

/**
 * Keep shared-link server parameters short without losing explicit local HTTP URLs.
 * Consumers normalize a bare host back to HTTPS during link handling.
 */
export function compactServerUrlForLink(input?: string | null): string | undefined {
  const origin = normalizeServerUrl(input || undefined);
  if (!origin) return undefined;
  try {
    const url = new URL(origin);
    return url.protocol === 'https:' ? url.host : origin;
  } catch {
    return origin;
  }
}

/**
 * Convenience for display/lookup.
 */
export function hostFromServerUrl(input?: string): string | undefined {
  const origin = normalizeServerUrl(input);
  if (!origin) return undefined;
  try {
    return new URL(origin).host;
  } catch {
    return undefined;
  }
}
